import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { Footer, FooterButtons } from '@/design_system/Footer';
import IconArrow from '@/icons/Arrow.svg';
import IconClock from '@/icons/Clock.svg';
import { ArticleContextData, useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { ArticleErrorsFilter } from '@/routes/Requests/contexts/errors';
import { RequestContextData } from '@/routes/Requests/contexts/interfaces';
import {
  Step,
  useMultiStepCreationContext,
} from '@/routes/Requests/contexts/MultiStepCreationContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import useViewPort from '@/utils/useViewport';

import { articleDetailsErrors } from '../../Request/Article/ArticleInformationFormDrawer';

const BackButton = ({ onPress }: { onPress: () => void }) => {
  const { isMobile } = useViewPort();
  const { isFirstStep } = useMultiStepCreationContext();

  return (
    <Button
      variant="secondary"
      size="medium"
      onPress={onPress}
      disabled={isFirstStep}
      style={{ width: isMobile ? '100%' : 'auto' }}
    >
      <IconArrow left />
      <Trans id="requests.new.actions.back">Back</Trans>
    </Button>
  );
};

const NextButton = ({ onPress }: { onPress: () => void }) => {
  const { isMobile } = useViewPort();

  return (
    <Button
      variant="primary"
      size="medium"
      onPress={onPress}
      style={{ width: isMobile ? '100%' : 'auto' }}
    >
      <Trans id="requests.new.actions.next">Next</Trans>
      <IconArrow right />
    </Button>
  );
};

const SaveButton = ({ isEditing, onPress }: { isEditing?: boolean; onPress: () => void }) => {
  const { isMobile } = useViewPort();

  return (
    <Button
      variant="primary"
      size="medium"
      onPress={onPress}
      dataTrackingId="requests.new.articles.drawer.actions.save"
      style={{ width: isMobile ? '100%' : 'auto' }}
    >
      {isEditing ? (
        <Trans id="requests.new.articles.drawer.actions.save">Save</Trans>
      ) : (
        <Trans id="requests.new.articles.drawer.actions.add">Add item</Trans>
      )}
    </Button>
  );
};

export const NewArticleFooter = ({
  isEditing,
  close,
  scrollAlertIntoView,
}: {
  isEditing: boolean;
  close: () => void;
  scrollAlertIntoView: () => void;
}) => {
  const { isMobile } = useViewPort();
  const { view: requestView } = useRequestContext();
  const { view, state } = useArticleContext();
  const { isFirstStep, currentStep, onNextStep, onPreviousStep, isLastStep } =
    useMultiStepCreationContext();

  const handleSaveForLater = () => {
    const { hasError } = state.errors.check(saveForLaterErrorsFilter());

    if (hasError) {
      scrollAlertIntoView();

      return;
    }

    state.errors.check(null);
    close();
  };

  const handleSave = () => {
    const { hasError } = state.errors.check(creationErrorsFilter(view, requestView));

    if (hasError) {
      scrollAlertIntoView();

      return;
    }

    state.errors.check(null);
    close();
  };

  const handleNext = () => {
    const { hasError } = state.errors.check(
      creationErrorsFilterByStep(view, requestView, currentStep)
    );

    if (hasError) {
      scrollAlertIntoView();

      return true;
    }

    state.errors.check(null);
    onNextStep();
  };

  const handlePrevious = () => {
    onPreviousStep();
  };

  return (
    <Footer>
      {!isMobile && !isFirstStep && <BackButton onPress={handlePrevious} />}
      <FooterButtons>
        {!requestView.client.dropoffSignature.enabled && (
          <Button
            variant="secondary"
            size="medium"
            onPress={handleSaveForLater}
            dataTrackingId="requests.new.articles.drawer.actions.save-for-later"
          >
            <IconClock />
            <Trans id="requests.new.articles.drawer.actions.save-for-later">Save for later</Trans>
          </Button>
        )}
        {isMobile && <BackButton onPress={handlePrevious} />}
        {!isLastStep ? (
          <NextButton onPress={handleNext} />
        ) : (
          <SaveButton isEditing={isEditing} onPress={handleSave} />
        )}
      </FooterButtons>
    </Footer>
  );
};

export function saveForLaterErrorsFilter() {
  return {
    details: {
      product: true,
      productAttributes: {
        missingValue: true,
      },
    },
  } satisfies ArticleErrorsFilter;
}

export function creationErrorsFilter(
  view: ArticleContextData['view'],
  requestView: RequestContextData['view']
) {
  return {
    ...informationErrorsFilter(view),
    ...initialConditionErrorsFilter(view),
    ...servicesErrorsFilter(view, requestView),
    details: {
      ...informationErrorsFilter(view).details,
      ...servicesErrorsFilter(view, requestView).details,
    },
  } satisfies ArticleErrorsFilter;
}

function creationErrorsFilterByStep(
  view: ArticleContextData['view'],
  requestView: RequestContextData['view'],
  currentStep: Step
): ArticleErrorsFilter | null {
  if (currentStep.id === 'information') {
    return informationErrorsFilter(view);
  }
  if (currentStep.id === 'initial-conditions') {
    return initialConditionErrorsFilter(view);
  }
  if (currentStep.id === 'services') {
    return servicesErrorsFilter(view, requestView);
  }

  return null;
}

function informationErrorsFilter(view: ArticleContextData['view']) {
  return {
    details: {
      ...articleDetailsErrors(view),
      missingArticlePhotoForRefashion: false,
    },
    warranty:
      view.warranty.shown && view.warranty.editable
        ? {
            missingPurchaseDate:
              view.warranty.fields.purchaseDate.shown && view.warranty.fields.purchaseDate.required,
            invalidPurchaseDate: view.warranty.fields.purchaseDate.shown,
            missingPurchaseLocation:
              view.warranty.fields.purchaseLocation.shown &&
              view.warranty.fields.purchaseLocation.required,
            missingPurchaseCountry:
              view.warranty.fields.purchaseCountry.shown &&
              view.warranty.fields.purchaseCountry.required,
            missingPurchaseChannel:
              view.warranty.fields.purchaseChannel.shown &&
              view.warranty.fields.purchaseChannel.required,
            missingOrderNumber:
              view.warranty.fields.orderNumber.shown && view.warranty.fields.orderNumber.required,
            missingProofOfPurchase:
              view.warranty.fields.proofOfPurchase.shown &&
              view.warranty.fields.proofOfPurchase.required,
            missingProofOfPurchaseOrOrderNumber:
              view.warranty.fields.orderNumber.shown &&
              view.warranty.fields.proofOfPurchase.shown &&
              view.warranty.fields.proofOfPurchaseOrOrderNumber.required,
            missingProofOfAuthenticity:
              view.warranty.fields.proofOfAuthenticity.shown &&
              view.warranty.fields.proofOfAuthenticity.required,
            missingOverrideReason: view.warranty.fields.warrantyOverrideReason.required,
          }
        : undefined,
  } satisfies ArticleErrorsFilter;
}

function initialConditionErrorsFilter(view: ArticleContextData['view']) {
  return {
    condition: view.initialCondition.shown
      ? {
          questions: {
            noAnswers: true,
            missingText: true,
            missingSelect: true,
            answers: {
              missingSelect: true,
            },
            subQuestion: {
              noAnswers: true,
              missingText: true,
              missingSelect: true,
              answers: {
                missingSelect: true,
              },
            },
          },
        }
      : undefined,
    defects:
      view.defects.shown || view.initialCondition.shown
        ? {
            noDefects: view.defects.required,
            defects: {
              missingDescription: view.defects.fields.description.editable,
              missingPhotos:
                view.defects.fields.photo.shown &&
                view.defects.fields.photo.editable &&
                view.defects.fields.photo.required,
              missingContext:
                view.defects.fields.context.shown &&
                view.defects.fields.context.editable &&
                view.defects.fields.context.required,
              missingExceptionalCoverageReason:
                view.defects.fields.brandResponsibility.exceptionalCoverage.enabled &&
                view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable,
            },
          }
        : undefined,
  } satisfies ArticleErrorsFilter;
}

function servicesErrorsFilter(
  view: ArticleContextData['view'],
  requestView: RequestContextData['view']
) {
  return {
    details: {
      missingArticlePhotoForRefashion: view.details.articlePhoto.shown,
    },
    defects:
      view.defects.shown || view.initialCondition.shown
        ? {
            noDefects: view.defects.required,
            defects: {
              missingDescription: view.defects.fields.description.editable,
              missingPhotos:
                view.defects.fields.photo.shown &&
                view.defects.fields.photo.editable &&
                view.defects.fields.photo.required,
              missingContext:
                view.defects.fields.context.shown &&
                view.defects.fields.context.editable &&
                view.defects.fields.context.required,
              missingActions:
                view.defects.fields.toBeRepaired.shown &&
                view.defects.fields.toBeRepaired.editable &&
                view.services.actions.shown,
              missingExceptionalCoverageReason:
                view.defects.fields.brandResponsibility.exceptionalCoverage.enabled &&
                view.defects.fields.brandResponsibility.exceptionalCoverage.toggle.editable,
            },
          }
        : undefined,
    services: {
      choice: view.services.choice.shown
        ? {
            noChoice: true,
          }
        : undefined,
      actions: view.services.actions.shown
        ? {
            noActions: view.services.actions.canAddAction,
            actions: {
              missingDescription: view.services.actions.fields.description.editable,
              missingPhotos:
                view.services.actions.fields.photo.shown &&
                view.services.actions.fields.photo.editable &&
                view.services.actions.fields.photo.required,
              missingRefashionPhoto:
                view.services.actions.fields.photo.shown &&
                view.services.actions.fields.photo.editable,
              missingContext:
                view.services.actions.fields.context.shown &&
                view.services.actions.fields.context.editable &&
                view.services.actions.fields.context.required,
              missingOrganizationPrice:
                requestView.organizationPrice.shown &&
                view.services.actions.fields.organizationPrice.editable &&
                view.services.actions.fields.organizationPrice.required,
              organizationPriceUpdateInProgress:
                requestView.organizationPrice.shown &&
                view.services.actions.fields.organizationPrice.editable,
              spareParts: view.services.actions.fields.spareParts.shown
                ? {
                    quantityIsNull: view.services.actions.fields.spareParts.quantity.editable,
                    missingOrganizationPrice:
                      requestView.organizationPrice.shown &&
                      view.services.actions.fields.spareParts.organizationPrice.editable &&
                      view.services.actions.fields.spareParts.organizationPrice.required,
                    organizationPriceUpdateInProgress:
                      requestView.organizationPrice.shown &&
                      view.services.actions.fields.spareParts.organizationPrice.editable,
                  }
                : undefined,
              additionalInfo: {
                missingValue: view.services.actions.fields.additionalInfo.editable,
              },
            },
          }
        : undefined,
      dispatch:
        view.services.dispatch.shown && view.services.dispatch.editable
          ? {
              noWorkshopSelected: true,
            }
          : undefined,
      exchange: view.services.exchange.shown
        ? {
            missingReplacementProduct: view.services.exchange.replacementProduct.editable,
            missingReplacementProductColor: view.details.color.shown,
            missingReplacementProductSize: view.details.size.shown,
            missingStockLocation: view.services.exchange.stockLocation.editable,
            missingOrganizationPrice:
              requestView.organizationPrice.shown &&
              view.services.exchange.organizationPrice.editable &&
              view.services.exchange.organizationPrice.required,
            organizationPriceUpdateInProgress:
              requestView.organizationPrice.shown &&
              view.services.exchange.organizationPrice.editable,
          }
        : undefined,
    },
    thresholds: {
      organization:
        view.prices.thresholds.organization.shown && view.prices.thresholds.organization.editable,
      client: view.prices.thresholds.client.shown && view.prices.thresholds.client.editable,
    },
  } satisfies ArticleErrorsFilter;
}
